import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Pencil, Plus, Store as StoreIcon } from "lucide-react";
import { getUserFacingErrorMessage } from "@/api/userFacingError";
import { AdminBreadcrumb } from "@/components/admin/AdminBreadcrumb";
import { AdminDrawer } from "@/components/admin/AdminDrawer";
import { AdminDrawerFooter } from "@/components/admin/AdminDrawerFooter";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import {
  adminAddButtonStyle,
  adminDrawerBodyStyle,
  adminFieldStyle,
  adminInputStyle,
  adminLabelStyle,
  adminSelectStyle,
} from "@/components/admin/adminFormStyles";
import { FloatingToast } from "@/components/feedback/FloatingToast";
import { OperationalPager } from "@/components/operational/OperationalPager";
import { OperationalQueryState } from "@/components/operational/OperationalQueryState";
import { OperationalSearchField } from "@/components/operational/OperationalSearchField";
import { useCurrentUser } from "@/features/auth/useCurrentUser";
import { getOperationalCapabilities } from "@/features/auth/operationalCapabilities";
import { canMutateStores, isClientStoreUser } from "@/features/auth/roles";
import { useClients } from "@/features/client/hooks";
import {
  useCreateStore,
  useDeleteStore,
  useStorePage,
  useUpdateStore,
} from "@/features/store/hooks";
import type { Store } from "@/features/store/types";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { getStoreClientLabel, getValidStoreOffset } from "./lojasDisplay";
import "./LojasPage.css";

const STORE_PAGE_SIZE = 10;

type DrawerMode = "create" | "edit";

interface DrawerState {
  mode: DrawerMode;
  store?: Store;
}

type StoreQueryState = {
  offset: number;
  search: string;
};

function StoreLedgerSkeleton() {
  return (
    <div
      aria-label="Carregando lojas"
      className="operational-directory lojas-ledger lojas-ledger--skeleton"
      role="status"
    >
      <div className="lojas-ledger__skeleton-header" />
      {Array.from({ length: 5 }, (_, index) => (
        <div className="lojas-ledger__skeleton-row" key={index}>
          <span />
          <span />
          <span />
        </div>
      ))}
    </div>
  );
}

function StoreLedger({
  stores,
  isClientScopedView,
  canEditStores,
  isFetching,
  onEdit,
}: {
  stores: Store[];
  isClientScopedView: boolean;
  canEditStores: boolean;
  isFetching: boolean;
  onEdit: (store: Store) => void;
}) {
  const ledgerClassName = [
    "operational-directory",
    "operational-directory__updating",
    "lojas-ledger",
    isClientScopedView ? "lojas-ledger--scoped" : "lojas-ledger--global",
    canEditStores ? "lojas-ledger--editable" : "lojas-ledger--read-only",
  ].join(" ");

  return (
    <div
      aria-busy={isFetching}
      aria-label="Lojas"
      className={ledgerClassName}
      role="table"
    >
      <div className="lojas-ledger__header lojas-ledger__row" role="row">
        <span role="columnheader">Loja</span>
        {!isClientScopedView ? <span role="columnheader">Cliente</span> : null}
        <span role="columnheader">Endereço</span>
        {canEditStores ? (
          <span className="lojas-ledger__action-heading" role="columnheader">
            Editar
          </span>
        ) : null}
      </div>

      {stores.map((store) => (
        <div
          className="lojas-ledger__row lojas-ledger__data-row"
          key={store.id}
          role="row"
        >
          <span className="lojas-ledger__name" role="cell">
            {store.name}
          </span>
          {!isClientScopedView ? (
            <span className="lojas-ledger__client" role="cell">
              {getStoreClientLabel(store)}
            </span>
          ) : null}
          <span className="lojas-ledger__address" role="cell">
            {store.address}
          </span>
          {canEditStores ? (
            <span className="lojas-ledger__action-cell" role="cell">
              <button
                aria-label={`Editar loja ${store.name}`}
                className="lojas-ledger__edit"
                onClick={() => onEdit(store)}
                title="Editar loja"
                type="button"
              >
                <Pencil aria-hidden="true" size={16} strokeWidth={2} />
              </button>
            </span>
          ) : null}
        </div>
      ))}
    </div>
  );
}

export function LojasPage() {
  const navigate = useNavigate();

  const { data: currentUser } = useCurrentUser();
  const isClient = currentUser?.role === "client";
  const isStoreUser = isClientStoreUser(currentUser);
  const isClientScopedView = isClient || isStoreUser;
  const { canManageCatalog } = getOperationalCapabilities(currentUser);
  const canEditStores = canMutateStores(currentUser) || canManageCatalog;
  const canDeleteStores = canMutateStores(currentUser);
  const myClientId = currentUser?.client_id ?? "";

  const [drawer, setDrawer] = useState<DrawerState | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState<StoreQueryState>({
    offset: 0,
    search: "",
  });
  const [dismissedListError, setDismissedListError] = useState<unknown>(null);

  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [clientId, setClientId] = useState("");

  const debouncedSearch = useDebouncedValue(searchInput, 250).trim();
  const storePage = useStorePage({
    limit: STORE_PAGE_SIZE,
    offset: query.offset,
    search: query.search,
  });
  const { data: clientsData } = useClients({
    enabled: canEditStores && !isClientScopedView && drawer !== null,
  });

  const createMutation = useCreateStore();
  const updateMutation = useUpdateStore();
  const deleteMutation = useDeleteStore();

  const items = storePage.data?.items ?? [];
  const total = storePage.data?.total ?? 0;
  const clients = clientsData?.items ?? [];
  const hasRows = items.length > 0;
  const showRefetchError =
    hasRows && storePage.isError && dismissedListError !== storePage.error;

  useEffect(() => {
    setQuery((current) =>
      current.search === debouncedSearch
        ? current
        : { offset: 0, search: debouncedSearch },
    );
  }, [debouncedSearch]);

  useEffect(() => {
    if (!storePage.data || storePage.isPlaceholderData) return;
    const validOffset = getValidStoreOffset(
      storePage.data.total,
      query.offset,
      STORE_PAGE_SIZE,
    );
    if (validOffset === query.offset) return;
    setQuery((current) => ({ ...current, offset: validOffset }));
  }, [query.offset, storePage.data, storePage.isPlaceholderData]);

  useEffect(() => {
    if (!storePage.isError) setDismissedListError(null);
  }, [storePage.isError]);

  function openCreate() {
    if (!canEditStores) return;
    setName("");
    setAddress("");
    setClientId(isClient ? myClientId : "");
    setConfirmDelete(false);
    setDrawer({ mode: "create" });
  }

  function openEdit(store: Store) {
    if (!canEditStores) return;
    setName(store.name);
    setAddress(store.address);
    setClientId(store.client_id);
    setConfirmDelete(false);
    setDrawer({ mode: "edit", store });
  }

  function closeDrawer() {
    setDrawer(null);
    setConfirmDelete(false);
  }

  async function handleSubmit() {
    if (!canEditStores) return;
    if (!name.trim() || !address.trim() || !clientId) return;

    const payload = {
      name: name.trim(),
      address: address.trim(),
      client_id: clientId,
    };

    if (drawer?.mode === "create") {
      await createMutation.mutateAsync(payload);
    } else if (drawer?.mode === "edit" && drawer.store) {
      await updateMutation.mutateAsync({
        id: drawer.store.id,
        payload,
      });
    }
    closeDrawer();
  }

  async function handleDelete() {
    if (!canDeleteStores || !drawer?.store) return;
    await deleteMutation.mutateAsync(drawer.store.id);
    closeDrawer();
  }

  const isPending =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending;

  return (
    <AdminPageShell
      action={
        canEditStores ? (
          <button
            className="lojas-add-button"
            type="button"
            onClick={openCreate}
            style={adminAddButtonStyle}
          >
            <Plus size={16} strokeWidth={2} />
            Adicionar
          </button>
        ) : null
      }
      breadcrumb={
        <AdminBreadcrumb
          items={[
            { label: "Mais", onClick: () => navigate({ to: "/mais" }) },
            { label: "Lojas" },
          ]}
        />
      }
      icon={StoreIcon}
      size="standard"
      title="LOJAS"
    >
      <style>{`
        .lojas-directory {
          display: grid;
          gap: 0.75rem;
        }

        .lojas-directory__toolbar {
          width: 100%;
        }

        .lojas-ledger {
          --lojas-row-padding: 0.7rem var(--operational-row-inset);
          overflow: hidden;
          border-radius: var(--operational-corner-md);
          font-family: 'DM Sans', sans-serif;
        }

        .lojas-ledger--global.lojas-ledger--editable .lojas-ledger__row {
          grid-template-columns: minmax(10rem, 1.15fr) minmax(9rem, 0.85fr) minmax(14rem, 1.5fr) 2.75rem;
        }

        .lojas-ledger--global.lojas-ledger--read-only .lojas-ledger__row {
          grid-template-columns: minmax(10rem, 1.15fr) minmax(9rem, 0.85fr) minmax(14rem, 1.5fr);
        }

        .lojas-ledger--scoped.lojas-ledger--editable .lojas-ledger__row {
          grid-template-columns: minmax(11rem, 1fr) minmax(16rem, 1.65fr) 2.75rem;
        }

        .lojas-ledger--scoped.lojas-ledger--read-only .lojas-ledger__row {
          grid-template-columns: minmax(11rem, 1fr) minmax(16rem, 1.65fr);
        }

        .lojas-ledger__row {
          display: grid;
          min-width: 0;
          align-items: center;
          gap: 0.85rem;
          padding: var(--lojas-row-padding);
          border-bottom: 1px solid var(--login-border);
        }

        .lojas-ledger__row:last-child {
          border-bottom: 0;
        }

        .lojas-ledger__header {
          min-height: 2.75rem;
          background: color-mix(in srgb, var(--login-text) 3%, var(--login-surface));
          color: var(--login-muted);
          font-family: 'DM Mono', monospace;
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.07em;
          text-transform: uppercase;
        }

        .lojas-ledger__data-row {
          min-height: 4rem;
          background: var(--login-surface);
          transition: background var(--operational-motion-hover) ease;
        }

        .lojas-ledger__data-row:hover {
          background: color-mix(in srgb, var(--login-accent) 3.5%, var(--login-surface));
        }

        .lojas-ledger__name,
        .lojas-ledger__client,
        .lojas-ledger__address {
          min-width: 0;
          overflow-wrap: anywhere;
        }

        .lojas-ledger__name {
          color: var(--login-text);
          font-size: 0.98rem;
          font-weight: 700;
          line-height: 1.3;
        }

        .lojas-ledger__client,
        .lojas-ledger__address {
          color: var(--login-muted);
          font-size: 0.84rem;
          line-height: 1.42;
        }

        .lojas-ledger__action-heading {
          overflow: hidden;
          text-align: center;
        }

        .lojas-ledger__action-cell {
          display: flex;
          min-width: 0;
          justify-content: flex-end;
        }

        .lojas-ledger__edit {
          display: inline-flex;
          width: 2.75rem;
          height: 2.75rem;
          align-items: center;
          justify-content: center;
          justify-self: end;
          padding: 0;
          border: 1px solid transparent;
          border-radius: var(--operational-corner-sm);
          background: transparent;
          color: var(--login-accent);
          cursor: pointer;
        }

        .lojas-ledger__edit:hover {
          border-color: var(--login-border);
          background: color-mix(in srgb, var(--login-accent) 8%, transparent);
        }

        .lojas-ledger__edit:focus-visible {
          outline: 2px solid var(--login-accent);
          outline-offset: 2px;
        }

        .lojas-directory__empty {
          display: grid;
          min-height: 10rem;
          place-items: center;
          padding: 1.5rem;
          border: 1px solid var(--login-border);
          border-radius: var(--operational-corner-md);
          background: var(--login-surface);
          color: var(--login-muted);
          text-align: center;
        }

        .lojas-directory__empty p {
          margin: 0;
          font-size: 0.9rem;
        }

        .lojas-ledger--skeleton {
          padding: 0;
        }

        .lojas-ledger__skeleton-header {
          height: 2.75rem;
          border-bottom: 1px solid var(--login-border);
          background: color-mix(in srgb, var(--login-text) 3%, var(--login-surface));
        }

        .lojas-ledger__skeleton-row {
          display: grid;
          min-height: 4rem;
          grid-template-columns: 1.1fr 0.85fr 1.5fr;
          align-items: center;
          gap: 1rem;
          padding: var(--lojas-row-padding);
          border-bottom: 1px solid var(--login-border);
        }

        .lojas-ledger__skeleton-row:last-child {
          border-bottom: 0;
        }

        .lojas-ledger__skeleton-row span {
          height: 0.75rem;
          border-radius: 3px;
          background: color-mix(in srgb, var(--login-muted) 16%, transparent);
          animation: lojas-skeleton-pulse 1.2s ease-in-out infinite alternate;
        }

        @keyframes lojas-skeleton-pulse {
          to { opacity: 0.42; }
        }

        @media (max-width: 640px) {
          .lojas-ledger {
            --lojas-row-padding: 0.75rem var(--operational-row-inset);
          }

          .lojas-ledger__header {
            position: absolute;
            width: 1px;
            height: 1px;
            overflow: hidden;
            clip: rect(0 0 0 0);
            clip-path: inset(50%);
            white-space: nowrap;
          }

          .lojas-ledger--global.lojas-ledger--editable .lojas-ledger__data-row,
          .lojas-ledger--scoped.lojas-ledger--editable .lojas-ledger__data-row {
            grid-template-columns: minmax(0, 1fr) 2.75rem;
          }

          .lojas-ledger--global.lojas-ledger--read-only .lojas-ledger__data-row,
          .lojas-ledger--scoped.lojas-ledger--read-only .lojas-ledger__data-row {
            grid-template-columns: minmax(0, 1fr);
          }

          .lojas-ledger__data-row {
            min-height: 5.25rem;
            align-content: center;
            column-gap: 0.55rem;
            row-gap: 0.2rem;
          }

          .lojas-ledger__name,
          .lojas-ledger__client,
          .lojas-ledger__address {
            grid-column: 1;
          }

          .lojas-ledger__name {
            font-size: 1rem;
          }

          .lojas-ledger__client,
          .lojas-ledger__address {
            font-size: 0.84rem;
          }

          .lojas-ledger__action-cell {
            grid-column: 2;
            grid-row: 1 / span 3;
          }

          .lojas-ledger__skeleton-row {
            min-height: 5.25rem;
            grid-template-columns: 1fr;
            gap: 0.45rem;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .lojas-ledger__data-row,
          .lojas-ledger__skeleton-row span {
            transition: none;
            animation: none;
          }
        }
      `}</style>

      <main className="lojas-directory">
        <div className="lojas-directory__toolbar">
          <OperationalSearchField
            ariaLabel="Buscar lojas"
            onChange={setSearchInput}
            placeholder={
              isStoreUser
                ? "Buscar nas suas lojas..."
                : "Buscar por loja, endereço ou cliente..."
            }
            value={searchInput}
          />
        </div>

        <OperationalQueryState
          error={hasRows ? null : storePage.error}
          hasData={hasRows}
          isFetching={storePage.isFetching}
          isFiltered={Boolean(query.search)}
          isLoading={storePage.isLoading}
          onRetry={() => void storePage.refetch()}
          renderEmpty={({ filtered }) => (
            <div className="lojas-directory__empty">
              <p>
                {filtered
                  ? "Nenhuma loja encontrada para esta busca."
                  : isStoreUser
                    ? "Nenhuma loja vinculada ao seu usuário."
                    : "Nenhuma loja cadastrada."}
              </p>
            </div>
          )}
          renderSkeleton={StoreLedgerSkeleton}
        >
          <StoreLedger
            canEditStores={canEditStores}
            isClientScopedView={isClientScopedView}
            isFetching={storePage.isFetching}
            onEdit={openEdit}
            stores={items}
          />
          <OperationalPager
            ariaLabel="Paginação de lojas"
            isFetching={storePage.isFetching}
            itemLabel="loja"
            itemLabelPlural="lojas"
            limit={STORE_PAGE_SIZE}
            offset={query.offset}
            onPageChange={(page) =>
              setQuery((current) => ({
                ...current,
                offset: page * STORE_PAGE_SIZE,
              }))
            }
            totalItems={total}
            variant="numbered"
          />
        </OperationalQueryState>

        {canEditStores ? (
          <AdminDrawer
            onClose={closeDrawer}
            onSubmit={handleSubmit}
            open={drawer !== null}
            title={drawer?.mode === "create" ? "Nova Loja" : "Editar Loja"}
            visualTreatment="operational-sheet"
          >
            <div className="lojas-drawer-fields" style={adminDrawerBodyStyle}>
              <div style={adminFieldStyle}>
                <label style={adminLabelStyle}>Nome</label>
                <input
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Ex: Filial Centro"
                  style={adminInputStyle}
                  value={name}
                />
              </div>

              <div style={adminFieldStyle}>
                <label style={adminLabelStyle}>Endereço</label>
                <input
                  onChange={(event) => setAddress(event.target.value)}
                  placeholder="Ex: Rua das Flores, 123"
                  style={adminInputStyle}
                  value={address}
                />
              </div>

              {!isClient ? (
                <div style={adminFieldStyle}>
                  <label style={adminLabelStyle}>Cliente Vinculado</label>
                  <select
                    onChange={(event) => setClientId(event.target.value)}
                    style={adminSelectStyle}
                    value={clientId}
                  >
                    <option disabled value="">
                      Selecione um cliente...
                    </option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
            </div>

            <AdminDrawerFooter
              primaryClassName="lojas-drawer-primary"
              dangerClassName="lojas-drawer-danger"
              confirmDelete={confirmDelete}
              isPending={isPending}
              mode={drawer?.mode ?? "create"}
              onConfirmDelete={canDeleteStores ? handleDelete : undefined}
              onRequestDelete={
                canDeleteStores ? () => setConfirmDelete(true) : undefined
              }
              onSubmit={handleSubmit}
              submitDisabled={!clientId}
              submitLabel={drawer?.mode === "create" ? "Criar" : "Salvar"}
            />
          </AdminDrawer>
        ) : null}

        {showRefetchError ? (
          <FloatingToast
            actionLabel="Tentar novamente"
            message={getUserFacingErrorMessage(
              storePage.error,
              "Tente novamente em alguns instantes.",
            )}
            onAction={() => {
              setDismissedListError(storePage.error);
              void storePage.refetch();
            }}
            onDismiss={() => setDismissedListError(storePage.error)}
            title="Não foi possível atualizar as lojas"
            tone="error"
          />
        ) : null}
      </main>
    </AdminPageShell>
  );
}
